import type { MockMessage } from "@/dummy";
import type { RealtimeMessageEvent } from "@/lib/use-visitor-realtime";

export type AssignmentStatus = "pending" | "connected" | null;

/**
 * Estimated time to the operator's first reply, present only while `pending`.
 * Flat shape (matches the BE `VisitorAssignmentEstimate` — same on the query
 * and the realtime event); which field is set depends on `basis`:
 *   - `operator_load` + `etaMinutes` → a rough minutes figure (BE clamps 2..60)
 *   - `operator_load` + `soft`       → no reliable number → a soft phrase
 *   - `business_hours` + `availableAt` → ISO time the team next opens (out of
 *     hours / offline)
 */
export interface AssignmentEstimate {
  basis: string;
  etaMinutes?: number | null;
  availableAt?: string | null;
  soft?: boolean | null;
}

export interface Assignment {
  status: AssignmentStatus;
  operator?: { name: string; avatar?: string };
  /** Present only when `status === "pending"`. */
  estimate?: AssignmentEstimate;
}

export const EMPTY_ASSIGNMENT: Assignment = { status: null };

/**
 * Fold a realtime `assignment` event into a fresh `Assignment`. Returns
 * `null` when the event is not an assignment event (caller ignores it).
 * The event fully specifies the new status, so this is a pure map — no
 * dependence on the previous state.
 */
export function assignmentFromEvent(event: RealtimeMessageEvent): Assignment | null {
  const a = event.assignment;
  if (!a) return null;
  if (a.assignedStatus === "connected") {
    return { status: "connected", ...(a.operator ? { operator: a.operator } : {}) };
  }
  if (a.assignedStatus === "pending") {
    return { status: "pending", ...(a.estimate ? { estimate: a.estimate } : {}) };
  }
  return { status: null };
}

/** The `visitorChatAssignment` query result (seed / resync) as an `Assignment`; `null` before a result. */
export function assignmentFromQuery(
  a:
    | {
        assignedStatus?: string | null;
        operator?: { name: string; avatar?: string | null } | null;
        estimate?: AssignmentEstimate | null;
      }
    | null
    | undefined,
): Assignment | null {
  if (!a) return null;
  if (a.assignedStatus === "connected") {
    return {
      status: "connected",
      ...(a.operator
        ? {
            operator: {
              name: a.operator.name,
              ...(a.operator.avatar ? { avatar: a.operator.avatar } : {}),
            },
          }
        : {}),
    };
  }
  // The query returns the ETA `estimate` only while pending (BE best-effort).
  if (a.assignedStatus === "pending") {
    return { status: "pending", ...(a.estimate ? { estimate: a.estimate } : {}) };
  }
  return EMPTY_ASSIGNMENT;
}

/**
 * Format a `business_hours` estimate's ISO `availableAt` into a short,
 * locale-aware label for the visitor's timezone:
 *   - today    → just the time ("3:00 PM")
 *   - tomorrow → localized "tomorrow" + time
 *   - later    → short weekday + time ("Mon, 9:00 AM")
 * `now` is injectable for tests. Returns "" for an unparseable input.
 */
export function formatAvailableAt(iso: string, locale: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const time = new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(d);
  if (sameDay(d, now)) return time;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (sameDay(d, tomorrow)) {
    const rel = new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(1, "day");
    return `${rel}, ${time}`;
  }
  const weekday = new Intl.DateTimeFormat(locale, { weekday: "short" }).format(d);
  return `${weekday}, ${time}`;
}

/**
 * The centered system row for a connected/resolved assignment event, keyed
 * by the BE `messageId` so it reconciles 1:1 with the persisted history item
 * on reload. Returns `null` for pending (no persistent message) or when the
 * event carries no id.
 */
export function assignmentSystemRow(event: RealtimeMessageEvent): MockMessage | null {
  const a = event.assignment;
  if (!a?.messageId) return null;
  const base = {
    id: a.messageId,
    direction: "OUTBOUND" as const,
    text: "",
    ageLabel: "",
    ...(event.sentAt ? { createdAt: event.sentAt } : {}),
    sender: { kind: "system" as const, name: "System" },
  };
  if (a.assignedStatus === "connected") {
    return {
      ...base,
      system: {
        type: "OPERATOR_CONNECTED",
        ...(a.operator?.name ? { operatorName: a.operator.name } : {}),
        ...(a.operator?.avatar ? { operatorAvatar: a.operator.avatar } : {}),
      },
    };
  }
  if (a.assignedStatus === null) {
    return {
      ...base,
      system: {
        type: "CHAT_RESOLVED",
        ...(a.initiator ? { initiator: a.initiator } : {}),
      },
    };
  }
  return null;
}

/**
 * Localized ETA line for a pending assignment's `estimate`, or `null` when
 * there's nothing sensible to show (so the caller renders only the plain
 * "connecting…" line — no regression). Branches on VALUES, not key presence,
 * so it handles both the flat query shape (nulls for the irrelevant fields)
 * and the leaner realtime-event shape.
 */
export function assignmentEtaText(
  estimate: AssignmentEstimate,
  t: (key: string, values?: Record<string, string | number>) => string,
  locale: string,
): string | null {
  if (estimate.basis === "business_hours") {
    return estimate.availableAt
      ? t("assignment.eta.businessHours", { time: formatAvailableAt(estimate.availableAt, locale) })
      : null;
  }
  if (estimate.basis === "operator_load") {
    if (typeof estimate.etaMinutes === "number") {
      return t("assignment.eta.minutes", { minutes: estimate.etaMinutes });
    }
    if (estimate.soft) return t("assignment.eta.soft");
  }
  return null;
}
